<template>
  <TransitionRoot as="template" :show="props.open">
    <Dialog
      as="div"
      class="fixed z-50 w-screen h-screen overflow-hidden top-0 left-0"
      @close="closeModal"
      :open="props.open"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-sm transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div class="flex justify-center p-4 text-center mt-36 sm:items-center sm:p-0">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6"
            >
              <div v-if="!formSubmitted">
                <div class="mt-3 sm:mt-5">
                  <DialogTitle
                    as="h3"
                    class="text-base font-semibold leading-6 text-gray-900"
                    >Add A New Payment Method</DialogTitle
                  >

                  <div class="mt-8 grid grid-cols-4 gap-x-4 gap-y-6">
                    <!-- Card number input -->
                    <div class="col-span-4">
                      <label for="card-number" class="block text-sm font-medium text-gray-700"
                        >Card number</label
                      >
                      <div class="mt-1">
                        <input
                          type="text"
                          id="card-number"
                          name="card-number"
                          autocomplete="cc-number"
                          v-model="cardNumber"
                          @blur="validateCardNumber"
                          class="block w-full px-2 rounded-md h-10 outline-none border border-zinc-300 shadow-sm focus:border-[#2d8bffce] focus:ring-[#2d8bffce] sm:text-sm"
                        />
                        <p v-if="cardNumberError" class="text-red-600 text-xs mt-1">
                          Card number must have 16 digits.
                        </p>
                      </div>
                    </div>

                    <!-- Expiration month input -->
                    <div class="col-span-2">
                      <label
                        for="expiration-month"
                        class="block text-sm font-medium text-gray-700"
                        >Expiration month</label
                      >
                      <div class="mt-1">
                        <select
                          name="expiration-month"
                          id="expiration-month"
                          autocomplete="cc-exp-month"
                          class="block w-full rounded-md outline-none border border-gray-300 shadow-sm focus:border-[#2d8bffce] focus:ring-[#2d8bffce] sm:text-sm h-10 px-2"
                        >
                          <option value="">Month</option>
                          <option value="01">01 - January</option>
                          <option value="02">02 - February</option>
                          <option value="03">03 - March</option>
                          <option value="04">04 - April</option>
                          <option value="05">05 - May</option>
                          <option value="06">06 - June</option>
                          <option value="07">07 - July</option>
                          <option value="08">08 - August</option>
                          <option value="09">09 - September</option>
                          <option value="10">10 - October</option>
                          <option value="11">11 - November</option>
                          <option value="12">12 - December</option>
                        </select>
                      </div>
                    </div>

                    <!-- Expiration year input -->
                    <div class="col-span-2">
                      <label
                        for="expiration-year"
                        class="block text-sm font-medium text-gray-700"
                        >Expiration year</label
                      >
                      <div class="mt-1">
                        <select
                          name="expiration-year"
                          id="expiration-year"
                          autocomplete="cc-exp-year"
                          class="block w-full rounded-md outline-none border border-zinc-300 shadow-sm focus:border-[#2d8bffce] focus:ring-[#2d8bffce] sm:text-sm h-10 px-2"
                        >
                          <option value="" disabled selected>Year</option>
                          <option v-for="(year, index) in years" :key="index" :value="year">
                            {{ year }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <!-- CVC input -->
                    <div class="col-span-1 relative group">
                      <label for="cvc" class="text-sm font-medium text-gray-700 flex">
                        CVC
                        <InformationCircleIcon
                          class="ml-2 h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                        <span
                          class="absolute left-0 bottom-full mb-2 w-32 bg-gray-700 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition ease-in-out duration-200"
                        >
                          3-digit security code usually found on the back of your card. American
                          Express cards have a 4-digit code located on the front.
                        </span>
                      </label>
                      <div class="mt-1">
                        <input
                          type="text"
                          name="cvc"
                          id="cvc"
                          autocomplete="csc"
                          class="block w-full rounded-md outline-none border border-zinc-300 shadow-sm focus:border-[#2d8bffce] focus:ring-[#2d8bffce] sm:text-sm h-10 px-2"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Buttons -->
                <div class="mt-5 sm:mt-10 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-5">
                  <button
                    type="button"
                    class="inline-flex w-full justify-center rounded-md bg-[#2d8bffce] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#2d8bff81] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2d8bffce] sm:col-start-2"
                    @click="validateForm"
                  >
                    Update Card
                  </button>
                  <button
                    type="button"
                    class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                    @click="closeModal"
                  >
                    Cancel
                  </button>
                </div>
              </div>

              <!-- credit card successfully changed -->
              <div class="mx-auto" v-if="formSubmitted">
                <div
                  class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100"
                >
                  <CheckIcon class="h-6 w-6 text-green-600" aria-hidden="true" />
                </div>
                <div class="mt-3 text-center sm:mt-5">
                  <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">
                    Payment Method Successfully Updated
                  </DialogTitle>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500">
                      This payment method will be used for app access, and all future in-app
                      purchases.
                    </p>
                  </div>
                </div>
                <div class="mt-5 sm:mt-6">
                  <button
                    type="button"
                    class="inline-flex w-full justify-center rounded-md bg-[#2d8bffce] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#2d8bff81] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2d8bffce]"
                  >
                    Go back to dashboard
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { defineProps, defineEmits, onMounted, ref } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { InformationCircleIcon, CheckIcon } from '@heroicons/vue/24/outline';

// Define the open prop
const props = defineProps({
  open: Boolean
});

// Define the emit function
const emit = defineEmits(['update:open']);

// Function to reset the form
const resetForm = () => {
  cardNumber.value = '';
  cardNumberError.value = false;
  formSubmitted.value = false;
};

// Function to close the modal and reset the form
const closeModal = () => {
  resetForm();
  emit('update:open', false);
};

// Define the years data
const years = ref([]);
onMounted(() => {
  const currentYear = new Date().getFullYear();
  for (let i = 0; i <= 20; i++) {
    years.value.push((currentYear + i).toString());
  }
});

// Define input properties
const cardNumber = ref('');
const cardNumberError = ref(false);
const formSubmitted = ref('');

// Define validation methods
const validateCardNumber = () => {
  if (!cardNumber.value || cardNumber.value.length !== 16) {
    cardNumberError.value = true;
    return false;
  }
  cardNumberError.value = false;
  return true;
};

const validateForm = () => {
  if (validateCardNumber()) {
    formSubmitted.value = true;
    cardNumber.value = '';
  }
};
</script>
